import { microFormat } from "@/Common/Helpers/MicroFormat";
import { TemplateUrlFormatter } from "@/Data/DataReports/TemplateUrlFormatter";
import { nsMatrix } from "@/Namespaces";
import { htmlEncode, IGetEditValue, isEmptyOrNull, ISetEditValue, PropertyItem, resolveUrl, sanitizeHtml, trimToNull, Widget, WidgetProps } from "@serenity-is/corelib";

export interface StaticTemplateUrlBlockOptions {
    text?: string;
    showIfTokenNull?: boolean;
    showIfTokenEmpty?: boolean;
    showIfTokenZero?: boolean;
    showIfTokenNegative?: boolean;
    url?: string;
    cssClass?: string;
    target?: string;
}

type ValueCheck = {
    anyNull: boolean;
    anyEmpty: boolean;
    anyZero: boolean;
    anyNegative: boolean;
};

export class StaticTemplateUrlBlock extends Widget<StaticTemplateUrlBlockOptions> implements IGetEditValue, ISetEditValue {
    static override[Symbol.typeInfo] = this.registerEditor(nsMatrix, [IGetEditValue, ISetEditValue]);

    static override createDefaultElement() { return document.createElement("div"); }

    constructor(options: WidgetProps<StaticTemplateUrlBlockOptions>) {
        options ??= {};

        if (trimToNull(options.text) == null)
            options.target = "_blank";

        super(options);

        this.domNode.classList.add("align-self-center");
    }

    protected value: string | null = null;
    protected item: any = null;
    protected property: PropertyItem | null = null;

    setEditValue(source: any, property: PropertyItem): void {
        this.value = source[property.name];
        this.item = source;
        this.property = property;

        this.updateElement();
    }

    getEditValue(property: PropertyItem, target: any): void {
        target[property.name] = this.value;
    }

    protected checkIfShouldRender({anyNull, anyEmpty, anyZero, anyNegative}: ValueCheck) {
        if (this.options.showIfTokenNull == false && anyNull)
            return false;

        if (this.options.showIfTokenEmpty == false && anyEmpty)
            return false;

        if (this.options.showIfTokenZero == false && anyZero)
            return false;

        return !(this.options.showIfTokenNegative == false && anyNegative);
    }

    protected checkValue(opt: { value: any; checks: ValueCheck }) {
        if (opt.value == null)
            opt.checks.anyNull = true;

        if (typeof opt.value == 'string' && opt.value.trim() == '')
            opt.checks.anyEmpty = true;

        if ((typeof opt.value == 'number' && opt.value == 0) || (typeof opt.value == 'string' && opt.value.trim() == '0'))
            opt.checks.anyZero = true;

        if (opt.value < 0)
            opt.checks.anyNegative = true
    }

    protected updateElement() {
        let url: string;
        let checks: ValueCheck = {
            anyNull: false,
            anyEmpty: false,
            anyZero: false,
            anyNegative: false
        };

        if (this.options.url == null)
            url = (this.value ?? '').toString();
        else
            url = microFormat(this.options.url, this.item, {
                handler: (token, data, offset, str) => {
                    const handled = TemplateUrlFormatter.microFormatUrlHandler(token, data, offset, str);
                    this.checkValue({value: data[token], checks});
                    return handled;
                }
            });

        if (!this.checkIfShouldRender(checks)) {
            this.domNode.innerHTML = '';
            return;
        }

        if (url && url.startsWith('~/')) {
            url = resolveUrl(url);
        }

        let txt: string;
        if (isEmptyOrNull(this.options.text)) {
            if (this.property?.name)
                txt = microFormat("{{" + this.property.name + "}}", this.item, {
                    htmlEncode: true,
                    handler: (token, data, _offset, _str) => {
                        const req = data[token];
                        this.checkValue({value: req, checks});
                        return req;
                    }
                });
            else
                txt = htmlEncode(url);
        } else {
            txt = microFormat(this.options.text, this.item, {
                htmlEncode: true,
                handler: (token, data, _offset, _str) => {
                    const req = data[token];
                    this.checkValue({value: req, checks});
                    return req;
                }
            });
        }

        if (!this.checkIfShouldRender(checks)) {
            this.domNode.innerHTML = '';
            return;
        }

        const content = <a href={url} target={this.options.target}
            class={this.options.cssClass ? microFormat(this.options.cssClass, this.item) : undefined}
            dangerouslySetInnerHTML={{__html: sanitizeHtml(txt)}}></a>;

        this.element.empty().append(content);
    }
}
